import { NavLink } from 'react-router-dom';
import { LuChevronLeft, LuChevronRight, LuMoon, LuStore, LuSun } from 'react-icons/lu';
import { appRoutes } from '../../routes';
import { useUserContext } from '../../context/userContext';
import { useTheme } from '../../context/themeContext';
import { useTr } from '../../context/langContext';
import LangToggle from '../../components/ui/lang-toggle';
import { webStrings } from '../../i18n/web-strings';

export const SIDE_NAV_EXPANDED = 248;
export const SIDE_NAV_COLLAPSED = 68;

/** Desktop navigation: owner-tinted rail with every destination (primary first, then the rest). */
export default function SideNav({
    collapsed,
    onToggleCollapsed,
    onPickOutlet,
}: {
    collapsed: boolean;
    onToggleCollapsed: () => void;
    onPickOutlet: () => void;
}) {
    const { outlet, outlets, accountName } = useUserContext();
    const { isDark, toggle } = useTheme();
    const tr = useTr();
    const themeLabel = tr(webStrings, isDark ? 'lightMode' : 'darkMode');
    const width = collapsed ? SIDE_NAV_COLLAPSED : SIDE_NAV_EXPANDED;

    return (
        <aside
            className="fixed inset-y-0 left-0 z-40 flex flex-col border-r border-line bg-surf2 transition-[width] duration-200"
            style={{ width }}
        >
            <div className="flex items-center gap-2 px-3 py-4">
                <img src={`${process.env.PUBLIC_URL}/resto-mark.png`} alt="" className="h-8 w-8 rounded-lg object-contain" />
                {!collapsed && (
                    <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-ink">{tr(webStrings, 'appName')}</p>
                        <p className="truncate text-2xs text-ink3">{tr(webStrings, 'ownerConsole')}</p>
                    </div>
                )}
            </div>

            <button
                onClick={onPickOutlet}
                disabled={outlets.length < 2}
                className="mx-2 mb-2 flex items-center gap-2 rounded-xl bg-owner-bg px-3 py-2 text-left disabled:cursor-default"
                title={outlet?.name ?? tr(webStrings, 'outlet')}
            >
                <LuStore className="shrink-0 text-owner-ink" size={17} />
                {!collapsed && (
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold text-owner-ink">
                        {outlet?.shortName || outlet?.name || tr(webStrings, 'outlet')}
                    </span>
                )}
                {!collapsed && outlets.length > 1 && <LuChevronRight className="shrink-0 text-owner-ink" size={15} />}
            </button>

            <nav className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
                {appRoutes
                    .filter((r) => r.group !== 'hidden')
                    .map(({ to, name, icon: Icon, group }, index, all) => (
                        <div key={to}>
                            {index > 0 && all[index - 1].group !== group && <div className="my-2 border-t border-line" />}
                            <NavLink
                                to={to}
                                title={name(tr)}
                                className={({ isActive }) =>
                                    `mb-0.5 flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-colors ${
                                        isActive ? 'bg-owner text-white' : 'text-ink2 hover:bg-surf hover:text-ink'
                                    }`
                                }
                            >
                                <Icon size={18} className="shrink-0" />
                                {!collapsed && <span className="truncate">{name(tr)}</span>}
                            </NavLink>
                        </div>
                    ))}
            </nav>

            <div className="border-t border-line p-2">
                <button
                    onClick={toggle}
                    className="mb-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-semibold text-ink2 hover:bg-surf"
                    title={themeLabel}
                >
                    {isDark ? <LuSun size={18} /> : <LuMoon size={18} />}
                    {!collapsed && <span>{themeLabel}</span>}
                </button>
                <LangToggle
                    compact={collapsed}
                    className={`mb-1 w-full rounded-xl px-3 py-2.5 text-[13px] text-ink2 hover:bg-surf ${
                        collapsed ? 'justify-center' : 'gap-3'
                    }`}
                />
                <button
                    onClick={onToggleCollapsed}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-semibold text-ink3 hover:bg-surf"
                >
                    {collapsed ? <LuChevronRight size={18} /> : <LuChevronLeft size={18} />}
                    {!collapsed && <span className="truncate">{accountName}</span>}
                </button>
            </div>
        </aside>
    );
}
